import {
  isMobile,
  isMantle,
  isAffiliate,
  isAppBaseRoute,
  isCore,
  isCorePaid,
  isWebWinback,
  isEnLanguage,
  isInApp,
  isWinback,
  isEmailMain,
  isIOS,
  isUSWinback,
  isCountry,
  isUS,
  isIntl,
  isDeLanguage,
  isCoreDirect,
  isIntlMantle,
  isUSInAppWinback,
  isAffiliateWithoutTrial,
  isDeEmailMain,
  isEsEmailMain,
  isInAppWinback,
  isHM,
  isDE,
  isDPP,
  isAppAndroidBase,
  isDirect,
  isReferralsBuyflow,
  isCorePaidWithoutAddWordsSEM,
  isAffiliateWithTrial,
  isReferralsRoute,
  isEsLanguage,
  isAppEmail,
  isClinicalRoute,
  isPreSignup,
  isSocialSharedRoute,
} from ".";
import { routeConstants } from "@utils/constants";
import getStore, { CoreReduxState } from "../redux/store";

import { isEmailWinback, isFromEmail, isUSEmail } from "./email";
import { checkSegments } from "./util";
import {
  EligibleStateCodeNoomClinical,
  INELIGIBLE_HEALTH_RISKS,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { getCourseDuration } from "@utils/calculateGoals";
import { getMeristemContext, getSubdivision } from "@utils/meristemContext";
import { getTrackingParams } from "../services/VisitTracker";
import { canRetrieveWeight } from "../refactoredSurvey";
import { getSingleOffers } from "../../hooks/useOffersContext";
import { getSingleAddOns } from "../../hooks/useAddOnsContext";
import { getSingleInAppOffers } from "../../hooks/useInAppOffersContext";
import { appConfig } from "src/config";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import { calculateBMIImperial } from "src/primitive/bmi";
import { isAppAndroid, isAppIos, isAppIosBase } from "./in-app";
import { isMealPlan, isMealWorkoutBundle, isWorkoutPlan } from "../addons";
import { isInExperimentConflictingWithSummitCoursePack } from "../api/addons/addons";
import { getIOSMessageHandlers } from "../in-app/messageHandlers";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  FeatureFlags,
  getFeatureFlagValue,
} from "src/pageDefinitions/featureFlags";

import {
  getPromoCodeState,
  isPromoCodePersisted,
} from "../redux/slices/promoCode";
import { TELEHEALTH_FREE_PLAN_ID } from "src/funnels/payment-en/pages/telehealthRedirects";
import {
  getMultiUserNoomPlanId,
  planDurationMonthsToTelehealthNoomPlanId,
} from "@utils/plansMapping";
import { loadActiveSubscriptionDataState } from "../redux/slices/activeSubscriptionData";
import { getMeristemConfigValue } from "../meristemConfig";
import {
  DE_COURSE_PACK_ELIGIBLE_COUNTRIES,
  EN_COURSE_PACK_ELIGIBLE_COUNTRIES,
} from "@utils/userSegment/constants";
import { getPageSet } from "src/pageDefinitions/pageSets";
import { noomSummitSurveyLayer } from "src/pageDefinitions/goto/constants";
import { isMultiUserPlan } from "../multiUserPlan";
import {
  loadEquivalentMultiUserPlan,
  loadProratedRefundData,
} from "../api/noomTogether";
import { isCheckoutTimerExpired } from "../checkoutTimer";
import { isNewBaselineCoursePackVariant } from "../paymentAddon";
import { getURLParams } from "../urlParams";
import zipState from "zip-state";
import { hasFallbackPromotionalOfferConfiguration } from "../fallbackPromotionalOffer";
import { getCampaignFromStorage } from "../api/campaign";
import {
  selectAMCoursepack,
  selectMWCoursepack,
  selectNoomClinical,
} from "src/utils/redux/slices/paymentAddon";
import { freeMonthsOfferConfiguration } from "../freeMonthsOffer";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_MONTHLY_PLAN_ID = "option_d_20221201_Ex1084var1email";

const devModeFlagSet =
  new URLSearchParams(window.location.search).get("idm") === "true";

export function isDevMode() {
  return devModeFlagSet;
}

export function isPrerender() {
  // eslint-disable-next-line no-underscore-dangle
  return !!(window as any).__prerender;
}

export function evalResultWithFeatureFlag(
  result: boolean,
  flag: keyof FeatureFlags
) {
  const flagValue = getFeatureFlagValue(flag) as boolean;
  if (flagValue === undefined) {
    return result;
  }
  return flagValue;
}

export const shouldEnrollUser = (state?: CoreReduxState) => {
  return !isInApp(state) && !isAppEmail(state);
};

export const isHMEligible = () => isUS() && isEnLanguage();

export const isNoomProgramQuestionEligible = (state?: CoreReduxState) =>
  isHMEligible() && isDirect(state) && isIOS();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams.get("had_itunes_sub") === "true";
};

const disableLPSurveySegment = checkSegments({
  featureFlagOverride: "disableLPSurvey",
});

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01d,
    routeConstants.exsf01,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
    routeConstants.clinical,
  ],
  noneOf: [isHM, isInApp, disableLPSurveySegment],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [hadPreviousITunesSubscription, routeConstants.app14DayReturning],
});

export const isGooglePlayPlansEligible = () => {
  // TODO (Brandon): enable when in-app is ready for release
  // const hasGooglePlayMessageHandler =
  //   !!getAndroidMessageHandlers()?.buyflowPurchaseProduct ||
  //   ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  // return isAppAndroidBase() && hasGooglePlayMessageHandler;
  return false;
};

export const isITunesPlansEligible = () => {
  const hasITunesMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseProduct ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesMessageHandler;
};

export const isITunes1MonthDefaultPlanEligible = () => {
  const hasITunesDefaultPlanMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseNoDetails ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesDefaultPlanMessageHandler;
};

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  oneOf: [
    isITunes1MonthDefaultPlanEligible,
    isITunesPlansEligible,
    isGooglePlayPlansEligible,
  ],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [isAppAndroid, isBraintreeStripeiOSEligible],
});

const ffwdEligibleCurrencies = ["USD", "EUR", "GBP", "CAD", "AUD"];
export const isCurrencyFastForwardEligible = () => {
  const { currency = "USD" } = getStore().getState().serverContext;
  return ffwdEligibleCurrencies.includes(currency);
};

const isUSOnlyFastForwardEligibleRoute = checkSegments({
  allOf: [isUS],
  oneOf: [isEmailMain, isEmailWinback, isReferralsRoute],
});

export const hasPromoCodeIntl = checkSegments({
  allOf: [hasPromoCode, isIntl],
});

export const isFastForwardEligible = checkSegments({
  allOf: [isEnLanguage, isUS],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
    isUSOnlyFastForwardEligibleRoute,
  ],
});

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isMantle, isWebWinback, routeConstants.emailMain],
});

export const isNoomPremiumSubscriptionEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
});

export const isFreeSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWebWinback, isEmailMain],
});

const isLanguageNoomPremiumEligible = checkSegments({
  oneOf: [isEnLanguage, isDeLanguage, isEsLanguage],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isLanguageNoomPremiumEligible],
  oneOf: [
    isMantle,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isAppIosBase, isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    isWebWinback,
    isAppAndroidBase,
  ],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [isMobile, isMantle, isFromEmail, isWinback, isInAppWinback],
});

export const userHasPurchasedNoomPremium = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremium],
});

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  oneOf: [userHasPurchasedNoomPremium, hasBoughtMealAndWorkoutPlan],
});

export const navigateToNoomPremiumConsultationSurvey = checkSegments({
  allOf: [userHasPurchasedNoomPremium],
  oneOf: [
    isMantle,
    routeConstants.affiliateTrialNPB,
    isReferralsBuyflow,
    isEmailWinback,
    isWinback,
    isAppAndroidBase,
  ],
});

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isMantle],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isMantle,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInAppWinback,
    isUSInAppWinback,
  ],
  allOf: [canRetrieveWeight],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [isMantle, isUSWinback, isUSEmail, isUSInAppWinback],
});

export const isShowingPaymentGraphExtraContent = checkSegments({
  oneOf: [isMantle, isUSWinback, isInAppWinback],
});

// TODO: Remove isDeLanguage once Noom Premium and Addons can successfully charge SEPA
// https://noomhq.atlassian.net/browse/GT-1256
export const isSEPAEligible = checkSegments({
  allOf: [isDE, isDeLanguage],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (state = getStore().getState()) => {
  const surveyAnswers = getSurveyAnswers(state);
  const { weight, idealWeight, weightKg, idealWeightKg } = surveyAnswers;
  const courseDuration = getCourseDuration(surveyAnswers);

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlMantle()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.ndpp,
    isGooglePlayPlansEligible,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isInAppWinback,
    isAppIosBase,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    routeConstants.hmReferrals,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isMantle, isEmailMain, isHM, isInApp],
});

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (!state.serverContext.trial_fees) {
    return false;
  }
  if (isEnLanguage()) {
    return !isTrialFeeWaived() && !hasNoTrial();
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxEnabled = checkSegments({
  noneOf: [
    isInApp,
    isCorePaidWithoutAddWordsSEM,
    isWinback,
    routeConstants.affiliateNoTrial,
  ],
  featureFlagOverride: "togglePromoCode",
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [isInApp],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isMantle, isWinback],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [isMantle, isWinback, routeConstants.hmPaid, routeConstants.hmDirect],
});

export const isMWCoursepackVisibleOnCheckout = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers],
  oneOf: [
    mwCoursepackOptedIn,
    // IMPORTANT: we are explicitly allowing the mental wellness coursepack on the checkout page for email-main users
    checkSegments({
      allOf: [isEnLanguage, isUS, isEmailMain],
    }),
  ],
});

export const isBMINoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { heightFeet, heightInch, weight } = surveyAnswers;
  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weight !== undefined
  ) {
    const currentBMI = calculateBMIImperial(heightFeet, heightInch, weight);
    return currentBMI && currentBMI >= 27;
  }
  return false;
};

export const isCurrentHealthRiskNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { currentHealthRiskMedical, currentHealthRiskMedicalMale } =
    surveyAnswers;

  if (
    currentHealthRiskMedical !== undefined ||
    currentHealthRiskMedicalMale !== undefined
  ) {
    const answers = [
      ...(currentHealthRiskMedical || []),
      ...(currentHealthRiskMedicalMale || []),
    ];

    const ineligible_answers = answers.filter((answer) =>
      INELIGIBLE_HEALTH_RISKS.includes(answer)
    );

    return !ineligible_answers.length;
  }
  return false;
};

export const isSixtyFiveOrOver = (
  { surveyAnswers } = getStore().getState()
) => {
  const { dateOfBirth } = surveyAnswers;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthDate.getFullYear();

  const currentMonth = today.getMonth();
  const birthMonth = birthDate.getMonth();

  const currentDate = today.getDate();
  const birthDay = birthDate.getDate();

  if (
    currentMonth < birthMonth ||
    (currentMonth === birthMonth && currentDate < birthDay)
  ) {
    age -= 1;
  }

  return age >= 65;
};

export const noomClinicalOptedIn = (
  { surveyAnswers } = getStore().getState()
) => {
  const { medicationFocused } = surveyAnswers;
  return !!medicationFocused;
};

export const isZipcodeNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    // Double check the state that they entered on the /zipcode question
    // Because it might not necessarily match the state we got from Meristem
    const state = zipState(zipcode);
    return state in EligibleStateCodeNoomClinical;
  }
  return false;
};

export const shouldAskMedicareEnrolled = (
  { surveyAnswers } = getStore().getState()
) => {
  const { ageRange } = surveyAnswers;
  if (ageRange !== undefined) {
    const parsedAgeRange = Number.parseInt(ageRange?.[0], 10);
    return (
      (!surveyAnswers.zipcode || isZipcodeNoomClinicalEligible()) &&
      parsedAgeRange &&
      parsedAgeRange >= 60 &&
      surveyAnswers.medicare == null
    );
  }
  return false;
};

export const isMedicareStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.medicare?.includes("yes");
};

export const noomClinicalCheckIfEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { geneticsAndBiology } = surveyAnswers;
  return !!geneticsAndBiology;
};

// How to Turn Off Telehealth: https://github.com/noom/eng-handbook/blob/master/teams/healthcare/playbook/turn_off_telehealth.md
// Check if Telehealth is enabled
export function isTelehealthEnabledGlobally() {
  return !!appConfig.TELEHEALTH_ENABLED;
}

export const hasAnsweredNoomClinicalSurvey = (
  { surveyAnswers } = getStore().getState()
) => {
  const { experienceWeightLoss } = surveyAnswers;
  return experienceWeightLoss !== undefined;
};

// How to update telehealth throttle: https://github.com/noom/eng-handbook/blob/master/teams/healthcare/playbook/update_telehealth_throttle.md
// Function that throttles telehealth eligibility. To do so, we have a throttle
// rate stored in the app config. We check using that throttle if the user is
// eligible, and then save that value to session state which we will use for
// future checks.
export function passesTelehealthSignupThrottle() {
  if (isNaN(appConfig.TELEHEALTH_BUYFLOW_THROTTLE)) {
    updateSessionState("browser", {
      passesTelehealthThrottle: true,
    });
    return true;
  }

  const previousThrottleCheck =
    getSessionState("browser").passesTelehealthThrottle;
  if (previousThrottleCheck !== undefined) {
    return previousThrottleCheck;
  }

  const passesTelehealthThrottle =
    Math.random() < appConfig.TELEHEALTH_BUYFLOW_THROTTLE;
  updateSessionState("browser", {
    passesTelehealthThrottle,
  });

  return passesTelehealthThrottle;
}

const isRouteNoomClinicalEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01,
    routeConstants.exsf01d,
    routeConstants.direct,
    routeConstants.emailWinback,
    routeConstants.emailMain,
    routeConstants.affiliateTrialNPB,
    routeConstants.app14DayWebMain,
    routeConstants.appEmail,
    routeConstants.winback,
    routeConstants.referrals,
    isSocialSharedRoute,
  ],
});

export const isUserSegmentNoomClinicalEligible = () => {
  if (isClinicalRoute()) {
    // Don't do the state or feature flag check
    return isTelehealthEnabledGlobally() && isUS() && isEnLanguage();
  }

  return (
    passesTelehealthSignupThrottle() &&
    isTelehealthEnabledGlobally() &&
    isUS() &&
    isEnLanguage() &&
    isRouteNoomClinicalEligible()
  );
};

const isClinicalSurveyEligibleByMedicareStatus = () => {
  // If in the clinical route, we only enter clinical survey if user is not on medicare
  if (isClinicalRoute()) {
    return isMedicareStatusNoomClinicalEligible();
  }
  // If not in clinical route, we ask about medicare during the survey so we want to always
  // enter the survey
  return true;
};

export const isNoomClinicalSurveyEligible = checkSegments({
  allOf: [
    isUserSegmentNoomClinicalEligible,
    isBMINoomClinicalEligible,
    isClinicalSurveyEligibleByMedicareStatus,
  ],
});

export const shouldShowNoomClinicalCheckoutOffer = (
  state = getStore().getState()
): boolean => {
  const { promoCode, recommendedPlan } = state;
  return (
    isMedicareStatusNoomClinicalEligible(state) &&
    isNoomClinicalSurveyEligible(state) &&
    isCurrentHealthRiskNoomClinicalEligible(state) &&
    !promoCode.promoCodeIsVip &&
    recommendedPlan.has_trial &&
    // We show the noom med add-on at checkout if:
    // 1. The user opted into NC and entered a valid zipcode afterwards or the user didn't opt into NC
    // (we want them to give them another option to select the add-on)
    // 2. The user hasn't selected the AM coursepack.
    ((isZipcodeNoomClinicalEligible(state) && noomClinicalOptedIn(state)) ||
      !noomClinicalOptedIn(state)) &&
    !amCoursepackOptedIn()
  );
};

export const shouldPreselectNoomClinicalCheckoutOffer = checkSegments({
  allOf: [
    shouldShowNoomClinicalCheckoutOffer,
    isZipcodeNoomClinicalEligible,
    noomClinicalOptedIn,
  ],
});

export function isTelehealthPlan(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.noom_plan_id;
  const paidTelehealthPlans = Object.values(
    planDurationMonthsToTelehealthNoomPlanId
  );
  return (
    paidTelehealthPlans?.includes(noomPlanId) ||
    noomPlanId === TELEHEALTH_FREE_PLAN_ID
  );
}

export function userHasPurchasedNoomClinical(state = getStore().getState()) {
  const { purchasedNoomClinical, userData } = state;

  // TODO: Move to using a more reliable way like an API instead of using
  // this userdata flag to determine who purchased Noom Med
  return purchasedNoomClinical || userData.purchasedNoomClinical;
}

const TELEHEALTH_CONTEXT_TYPES = ["telehealth", "telehealth-followup"];

export function isTelehealth() {
  return TELEHEALTH_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return isTelehealth();
};

export const isHmSuperswitchEligible = (): boolean => {
  const { activeSubscriptionData } = getStore().getState();
  return (
    isEnLanguage() &&
    activeSubscriptionData.hasActiveSubscription &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.endsWith("en_US") &&
    !isMultiUserPlan(activeSubscriptionData?.planId) &&
    ((activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

isHmSuperswitchEligible.load = () => {
  const store = getStore();
  const { email } = store.getState().userData;
  if (email) {
    return getStore()
      .dispatch(loadActiveSubscriptionDataState({ email }))
      .then(() => {});
  }
  return Promise.resolve();
};

export const userHasAccessCode = (): boolean => {
  return !!getStore().getState().userData.access_code;
};

export const isInHouseSharePageEligible = checkSegments({
  allOf: [isEnLanguage, isUS, userHasAccessCode],
});

const startFetchingRequiredMultiUserPlanData = () => {
  return true;
};

startFetchingRequiredMultiUserPlanData.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
  const { billingCycleIntervalCount } =
    getStore().getState().activeSubscriptionData;

  const initialMultiUserNoomPlanId = getMultiUserNoomPlanId(
    billingCycleIntervalCount
  );
  // Explicitly don't await for this to not block pageset load
  Promise.all([
    loadProratedRefundData(),
    loadEquivalentMultiUserPlan(initialMultiUserNoomPlanId),
  ]);

  return Promise.resolve();
};

export const isEligibleForNoomTogether = checkSegments({
  allOf: [isEnLanguage, userHasAccessCode],
});

isEligibleForNoomTogether.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

const hasFamilyPlanEligibleSubscription = () => {
  const {
    isIAPsubscription,
    hasActiveSubscription,
    planId,
    billingCycleIntervalCount,
  } = getStore().getState()?.activeSubscriptionData;
  return (
    hasActiveSubscription &&
    billingCycleIntervalCount > 1 &&
    !isIAPsubscription &&
    !isMultiUserPlan(planId)
  );
};

hasFamilyPlanEligibleSubscription.load = () => {
  return getStore()
    .dispatch(loadActiveSubscriptionDataState({}))
    .then(() => {});
};

const isNotEnrolledInExR143 = (state: CoreReduxState) => {
  const { userData } = state;
  return !userData?.usedBaselinePriceForMultiUserPlan;
};

export const isFamilyPlanSurveyEligible = checkSegments({
  allOf: [
    isUS,
    isEnLanguage,
    hasFamilyPlanEligibleSubscription,
    startFetchingRequiredMultiUserPlanData,
    isNotEnrolledInExR143,
  ],
});

export const isEligibleForExitIntentModalEmail = () => {
  return isIntl() && isEmailMain() && getTrackingParams().utm_source === "drip";
};

export function hasPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);
  return !!promoCode?.promoCodeApplied;
}

export function hasVipPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);

  return hasPromoCode() && promoCode?.promoCodeIsVip;
}

export const hasOffers = (state: CoreReduxState) => {
  const { offers } = state;
  return offers.hasOffers;
};
export const isTaxExclusiveCountry = () => isCountry(["US", "CA"]);

export const hasSingleOffers = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleOffers(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleAddOns = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleAddOns(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleInAppOffers = () => {
  const { serverContext, userData } = getStore().getState();
  const singleOffers = getSingleInAppOffers(
    serverContext,
    userData.NoomPremiumSurveyCompleteDateISO
  );

  return singleOffers.length > 0;
};

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath");
};

export const isNoomPremiumEligible = () => {
  const promoCode = getPromoCodeState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();

  return !promoCode?.promoCodeIsVip && isNoomPremiumEligibleBySegments;
};

export function isEligibleForCheckoutTimer(state = getStore().getState()) {
  // Note(Pratik) inAppIos is the only route where they saw the original timer
  // but are not eligible for coursepacks to see the new timer due to itunes addon purchase
  // limitations so we will preserve their experience
  if (isAppIosBase()) {
    return true;
  }

  const originalCheckoutTimerCriteria =
    isMantle() &&
    !isEmailMain() &&
    !isEmailWinback() &&
    !state.recommendedPlan.isForcedPlan &&
    !isNewBaselineCoursePackVariant();

  const newCheckoutTimerCriteria =
    shouldShowCoursePackCheckoutOffers() &&
    Boolean(selectMWCoursepack(state.paymentAddon)) &&
    isNewBaselineCoursePackVariant() &&
    !isCheckoutTimerExpired();

  return originalCheckoutTimerCriteria || newCheckoutTimerCriteria;
}

export const requiresDoubleOptInForEmail = () => {
  return isCountry(["DE", "AT"]);
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};

isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUser = () => {
  const { winbackInfo } = getStore().getState();

  return ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus);
};

isWinbackUser.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isReUp = (state = getStore().getState()) => {
  const { winbackInfo } = state;
  return winbackInfo.willReup;
};

isReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

const isENCoursePackEligible = () => {
  return isCountry(EN_COURSE_PACK_ELIGIBLE_COUNTRIES) && isEnLanguage();
};

export const isDECoursePackEligible = () => {
  return isCountry(DE_COURSE_PACK_ELIGIBLE_COUNTRIES) && isDeLanguage();
};

const isLocaleCoursePackEligible = () => {
  return isENCoursePackEligible() || isDECoursePackEligible();
};

const isRouteCoursePackEligible = checkSegments({
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isEmailMain,
    isEmailWinback,
    isAppAndroidBase,
    isWinback,
    isAffiliate,
  ],
});

export function mwCoursepackOptedIn(state = getStore().getState()) {
  const { surveyAnswers } = state;
  return surveyAnswers.noomMoodBundle?.includes("yes");
}

export function amCoursepackOptedIn(state = getStore().getState()) {
  const { surveyAnswers } = state;
  return surveyAnswers.agingMetabolismCoursepack?.includes("yes");
}

export function shouldShowCoursePackSurveys(state = getStore().getState()) {
  const { recommendedPlan } = state;
  return (
    isLocaleCoursePackEligible() &&
    isRouteCoursePackEligible() &&
    recommendedPlan.has_trial &&
    !isReUp(state)
    // Originally we only showed course pack surveys if
    // you were not eligible for Noom Med. However we will support both
    // Moom Med and Mental Wellness, and selectively suppress
    // AM for now while 3 course packs support is built out.
    // && !shouldPreselectNoomClinicalCheckoutOffer()
  );
}

shouldShowCoursePackSurveys.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export function shouldShowCoursePackCheckoutOffers(
  state = getStore().getState()
) {
  const { promoCode } = state;
  return shouldShowCoursePackSurveys() && !promoCode.promoCodeIsVip;
}

export const shouldShowPaymentAddonCheckoutOffers = checkSegments({
  oneOf: [
    shouldShowCoursePackCheckoutOffers,
    shouldShowNoomClinicalCheckoutOffer,
  ],
});

export const shouldPreselectMWCoursepackCheckoutOffer = checkSegments({
  oneOf: [
    // Via buyflow coursepack surveys
    (state) =>
      shouldShowCoursePackCheckoutOffers(state) && mwCoursepackOptedIn(state),
    // Via email route modal
    ({ userData }) =>
      userData?.answerToFreeMWCPModal === "yes" &&
      userData?.checkoutDiscountedMWCPTimerExpirationTimestamp &&
      userData.checkoutDiscountedMWCPTimerExpirationTimestamp > Date.now(),
  ],
});

export const shouldPreselectAMCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, amCoursepackOptedIn],
});

export function acceptedMWCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes("mentalWellnessCoursepack") &&
    Boolean(selectMWCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedAMCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes("agingMetabolismCoursepack") &&
    Boolean(selectAMCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state) &&
    !shouldShowNoomClinicalCheckoutOffer()
  );
}

export function acceptedNoomClinicalOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes("noomClinical") &&
    Boolean(selectNoomClinical(paymentAddon)) &&
    shouldShowNoomClinicalCheckoutOffer(state)
  );
}

export const isNoomSummitEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [
    isInExperimentConflictingWithSummitCoursePack,
    isTelehealthPlan,
    userHasPurchasedNoomClinical,
    isInApp,
  ],
  oneOf: [
    isCore,
    isAffiliate,
    isReferralsBuyflow,
    isWinback,
    isEmailWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isNoomSummitDisplayableInStore = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
});

// Users who purchase Noom Summit can still visit the noom-summit-offer pagesets,
// and will be redirected to the confirmation page
export const isNoomSummitPagesetEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
});

export const isNoomSummitSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomSummitSurveyLayer,
  ],
});

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtMealAndWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isMealWorkoutBundle);
}

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

const isPreSelectedPlanLongerThan1Month = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 1;
};

const isPreSelectedPlanLongerThan2Months = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 2;
};

const isPreSelectedPlanTrial14Days = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.trial_duration === 14;
};

export const isLoadingPromoCodeModalEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

export function shouldRedirectToEmployerSurvey(
  { surveyAnswers } = getStore().getState()
) {
  const correctSurveyAnswer =
    surveyAnswers.employmentLifestyle?.includes("fullTime");
  const isCorrectTargeting = isEnLanguage() && isUS();
  const meristemConfigValue = getMeristemConfigValue(
    "redirectToEmployerSurvey"
  );

  return correctSurveyAnswer && (meristemConfigValue ?? isCorrectTargeting);
}

export function shouldRedirectToPostCheckoutContext() {
  return !!getMeristemConfigValue("usePostCheckoutContext");
}

export function shouldRedirectToHmAddonsContext() {
  return !!getMeristemConfigValue("useHmAddonsContext");
}

export function shouldRedirectHmReferralsContext() {
  return !!getMeristemConfigValue("useHmReferralsContext");
}

export const shouldShowHealthyPaceHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWinback],
});

export const shouldShowImportantEventHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWebWinback],
});

export const isEligibleForNursingOption = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

// ex472b feature. PR: https://github.com/noom/buyflow-client/pull/1356
export const shouldShowAlternatePostCheckoutHeadersText = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const shouldShowUnloadWarning = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const isEligibleForAgingMetabolismSurvey = (
  state = getStore().getState()
) => {
  if (!isUS() || isEmailWinback()) {
    return false;
  }
  if (isZipcodeNoomClinicalEligible(state) && noomClinicalOptedIn(state)) {
    return false;
  }
  const { ageRange, cognitiveExplore } = getSurveyAnswers(state);
  if (ageRange?.includes("20s")) {
    return false;
  }
  if (ageRange?.includes("30s")) {
    return (
      cognitiveExplore?.includes("aging") ||
      cognitiveExplore?.includes("metabolism")
    );
  }
  return true;
};

export const isEligibleForModularBuyflow = checkSegments({
  allOf: [isEnLanguage, isMantle],
  noneOf: [isHM],
});

const isEmailIntl = checkSegments({
  allOf: [isIntl],
  oneOf: [isEmailMain, isEmailWinback],
});

const isEmailWinbackUS = checkSegments({
  allOf: [isUS, isEmailWinback],
});

export const isFreeMonthsPlanOverrideEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isEmailIntl, isEmailWinbackUS],
});

export const isFreeMonthsPlanSelectEligible = checkSegments({
  allOf: [isEnLanguage, isEmailMain, isUS],
});

export const shouldShowFreeMonthsPlanSelect = () => {
  return !!freeMonthsOfferConfiguration() && isFreeMonthsPlanSelectEligible();
};

// Free month offers are shown to users in 2 different forms:
// 1. Free month plan override that overrides a shorter plan with a longer plan
// of cheaper or equal price when the user applies a promocode
//
// 2. Free month plan select which shows a 3 plan selection UI (monthly, middle length, long length)
// and discounts the long length plan to make it equal to the price of the middle length plan
// when the user applies a promocode
export const isFreeMonthsOfferEligible = checkSegments({
  oneOf: [isFreeMonthsPlanOverrideEligible, isFreeMonthsPlanSelectEligible],
});

export const isPlanSelectionEligible = checkSegments({
  oneOf: [
    shouldShowFreeMonthsPlanSelect,
    checkSegments({
      allOf: [
        isEmailMain,
        isUS,
        isPreSelectedPlanLongerThan1Month,
        isPreSelectedPlanTrial14Days,
      ],
    }),
  ],
});

export const shouldShowPlanSelectionSavings = checkSegments({
  allOf: [isPlanSelectionEligible, isPreSelectedPlanLongerThan2Months],
});

export const hasExistingAcccount = checkSegments({
  oneOf: [isAppEmail],
});

export const isMultiUserPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isWinback,
    isAppAndroidBase,
    isAffiliateWithTrial,
  ],
});

export function multiUserPlanOptedIn(
  state: CoreReduxState = getStore().getState()
) {
  const TWO_USER_PLAN = "twoUserPlan";
  const { nominationFamily } = getSurveyAnswers(state);

  return nominationFamily?.[0] === TWO_USER_PLAN;
}

export const shouldShowPrimedSupporterExperience = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isAffiliate,
    isAppBaseRoute,
    isReferralsBuyflow,
    isWebWinback,
    isEmailMain,
    isClinicalRoute,
  ],
});

export const isReferedFromUserPortal = () => {
  return getURLParams()?.redirected_from === "userportal";
};

export const isFallbackPromotionalOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage, isEmailMain],
});

const wasIncorrectPromoCodeEntered = (
  state: CoreReduxState = getStore().getState()
) => {
  return state?.promoCode?.promoCodeErrorType === "IncorrectPromoCode";
};

export const shouldShowFallbackPromotionalOffer = checkSegments({
  allOf: [
    isFallbackPromotionalOfferEligible,
    hasFallbackPromotionalOfferConfiguration,
    wasIncorrectPromoCodeEntered,
  ],
  noneOf: [shouldShowFreeMonthsPlanSelect],
});

export const shouldAllowPromoCodeRemoval = () => {
  if (
    isFallbackPromotionalOfferEligible() &&
    hasFallbackPromotionalOfferConfiguration()
  ) {
    return true;
  }

  return (
    (isPromoCodePersisted() || !!getCampaignFromStorage()) &&
    isEnLanguage() &&
    (isEmailMain() || isEmailWinback())
  );
};

export function canUseShareApi() {
  return (
    "canShare" in navigator &&
    navigator.canShare({ text: "anything", url: "anything" }) &&
    "share" in navigator
  );
}

export const isBillingReminderEligible = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isReferralsBuyflow,
    isAppIosBase,
  ],
  allOf: [isUS, isEnLanguage],
});

export const isEligibleForPreCheckoutFreeMWCPOffer = checkSegments({
  allOf: [isUS, isEnLanguage, isEmailMain, isPreSignup],
});

export const shouldShowEx1174 = () => {
  return !!getMeristemConfigValue("isEx1174Active");
};

export const isCareFirstRedirectEligible = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    ["MD", "DC", "DE", "VA"].includes(getSubdivision())
  );
};

export const isFromCareFirstEmailBlast = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    isFromEmail() &&
    getURLParams()?.utm_campaign === "cf_blast"
  );
};
